/**
 * "Connect with a Stylist" PDP CTA: opens Gorgias Chat on the conversation view with the message input pre-filled
 * with the product name, selected variant options (size/colour) and product URL.
 *
 * - Keeps the message/href in sync with the selected variant (the theme does not re-render the "button" block on
 *   variant change, so this listens to "variant:change" like dynamic-free-shipping.js does).
 * - On click, uses the GorgiasChat JS API (init -> open -> setPage('chat') -> setCurrentInput). Opening first matters
 *   on stores whose chat home is the self-service screen: setPage before open is reset back to that home.
 *   If the widget is not on the page, it follows the configured fallback link or the Gorgias deep link in the href.
 *
 * @see https://docs.gorgias.com/en-US/html-snippets-for-chat-customization-460197
 */
(function () {
  const ROOT_SELECTOR = '[data-gorgias-stylist-chat]';
  const GORGIAS_PARAMS = 'gs_open_chat&gs_chat_page=chat&gs_chat_message=';
  const GORGIAS_INIT_TIMEOUT = 4000;

  function parseOptionNames(root) {
    try {
      const names = JSON.parse(root.dataset.optionNames || '[]');
      return Array.isArray(names) ? names : [];
    } catch (error) {
      return [];
    }
  }

  function getVariantOptionValues(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options;
    }

    return [variant?.option1, variant?.option2, variant?.option3];
  }

  function buildVariantPart(root, variant) {
    if (!variant) {
      return '';
    }

    const optionNames = parseOptionNames(root);
    const optionValues = getVariantOptionValues(variant);
    const parts = [];

    optionNames.forEach((name, index) => {
      const value = optionValues[index];

      if (value == null || value === '' || value === 'Default Title') {
        return;
      }

      parts.push(String(name).toLowerCase().includes('size') ? `size ${value}` : String(value));
    });

    return parts.length ? ` in ${parts.join(', ')}` : '';
  }

  function buildMessage(root, variant) {
    const intro = (root.dataset.messageIntro || '').trim();
    const outro = (root.dataset.messageOutro || '').trim();
    const productName = root.dataset.productName || '';
    const productUrl = root.dataset.productUrl || '';

    let message = `${productName}${buildVariantPart(root, variant)}.`;

    if (intro) {
      message = `${intro} ${message}`;
    }

    if (outro) {
      message = `${message} ${outro}`;
    }

    return `${message} ${productUrl}`.trim();
  }

  function buildChatHref(root, variant, message) {
    const path = root.dataset.productPath || window.location.pathname;
    const variantParam = variant?.id ? `variant=${encodeURIComponent(variant.id)}&` : '';

    return `${path}?${variantParam}${GORGIAS_PARAMS}${encodeURIComponent(message)}`;
  }

  function update(root, variant) {
    const message = buildMessage(root, variant);
    const link = root.querySelector('a');

    root.dataset.message = message;

    if (link) {
      link.setAttribute('href', buildChatHref(root, variant, message));
    }
  }

  function waitForGorgias() {
    if (window.GorgiasChat && typeof window.GorgiasChat.init === 'function') {
      return Promise.resolve(window.GorgiasChat.init());
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Gorgias chat did not load in time.')), GORGIAS_INIT_TIMEOUT);

      window.addEventListener(
        'gorgias-widget-loaded',
        () => {
          clearTimeout(timeout);
          resolve(window.GorgiasChat?.init?.());
        },
        { once: true }
      );
    });
  }

  function showMessage(chat, message) {
    // "chat" always opens the message box. "conversation" can land on the self-service home
    // (logo / Track orders) when that home is enabled, which is the women's widget.
    if (typeof chat.setPage === 'function') {
      chat.setPage('chat');
    }

    if (typeof chat.setCurrentInput === 'function') {
      chat.setCurrentInput(message);
    }
  }

  function openChatWithMessage(message) {
    return waitForGorgias().then(() => {
      const chat = window.GorgiasChat;

      if (!chat) {
        throw new Error('GorgiasChat is unavailable.');
      }

      if (typeof chat.open === 'function') {
        chat.open();
      }

      showMessage(chat, message);

      // Opening can paint the home screen after these calls return, so apply again once it is open.
      if (typeof chat.on === 'function') {
        const onOpened = () => {
          showMessage(chat, message);

          if (typeof chat.off === 'function') {
            chat.off('widget:opened', onOpened);
          }
        };

        chat.on('widget:opened', onOpened);
      }
    });
  }

  function hasGorgiasOnPage() {
    return Boolean(window.GorgiasChat) || Boolean(document.querySelector('script[src*="gorgias"], #gorgias-chat-container'));
  }

  document.addEventListener('variant:change', (event) => {
    // Scoped to the product being viewed so a quick buy elsewhere on the page can't change it.
    const container = event.target?.closest?.('product-rerender');

    if (!container) {
      return;
    }

    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => update(root, event.detail?.variant));
  });

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.(`${ROOT_SELECTOR} a`);

    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    const root = link.closest(ROOT_SELECTOR);
    const message = root?.dataset.message || '';
    const fallbackHref = root?.dataset.fallbackHref;

    if (!hasGorgiasOnPage()) {
      if (fallbackHref) {
        event.preventDefault();
        window.location.assign(fallbackHref);
      }

      // Otherwise let the Gorgias deep link in the href proceed.
      return;
    }

    event.preventDefault();

    openChatWithMessage(message).catch(() => {
      // Widget failed to initialise: fall back to the configured link, or the deep link in the href.
      window.location.assign(fallbackHref || link.getAttribute('href'));
    });
  });
})();
